/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { logSafeErrorWithoutCustomerDataWrapper } from '@jira/polaris__lib-logging/src/logSafeErrorWithoutCustomerDataWrapper.tsx';
import { UnknownError } from '@jira/polaris__lib-errors/src/UnknownError.tsx';
import {
	getUserProperty,
	setUserProperties,
} from '@atlassian/jira-common-rest/src/api/latest/user-properties/index.tsx';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action } from '@atlassian/react-sweet-state';
import { CUSTOM_DATES_CUSTOMIZE_SPOTLIGHT_DISMISSED } from '../../../constants.tsx';
import type { Props, State } from '../../../types.tsx';
import { maybeWaitForPremiumModal } from '../../utils.tsx';

export const loadCustomDatesCustomizeSpotlightDismissed =
	(): Action<State, Props, Promise<boolean | undefined>> =>
	async ({ getState, setState }, { accountId }) => {
		if (!accountId) {
			return;
		}

		await maybeWaitForPremiumModal(getState);

		// User property is already loaded
		if (typeof getState().customDates.customizeSpotlightDismissed === 'boolean') {
			return getState().customDates.customizeSpotlightDismissed;
		}

		try {
			const customizeSpotlightDismissed = await getUserProperty<boolean>(
				accountId,
				CUSTOM_DATES_CUSTOMIZE_SPOTLIGHT_DISMISSED,
			);

			const state = getState();

			setState({
				...state,
				customDates: {
					...state.customDates,
					customizeSpotlightDismissed,
				},
			});

			return customizeSpotlightDismissed;
		} catch (error) {
			// Expected error, the property is not set yet
			if (error instanceof FetchError && error.statusCode === 404) {
				const state = getState();

				setState({
					...state,
					customDates: {
						customizeSpotlightDismissed: false,
					},
				});

				return false;
			}

			const errorMessage = `Failed to get user property with key: ${CUSTOM_DATES_CUSTOMIZE_SPOTLIGHT_DISMISSED}`;
			logSafeErrorWithoutCustomerDataWrapper(
				'polaris.custom-dates.customize.spotlight',
				errorMessage,
				error instanceof Error ? error : new UnknownError(errorMessage, error),
			);
		}
	};

export const setCustomDatesCustomizeSpotlightDismissed =
	(): Action<State, Props> =>
	async ({ getState, setState }, { accountId }) => {
		if (!accountId) {
			return;
		}

		const state = getState();

		setState({
			...state,
			customDates: {
				customizeSpotlightDismissed: true,
			},
		});

		try {
			await setUserProperties(accountId, CUSTOM_DATES_CUSTOMIZE_SPOTLIGHT_DISMISSED, 'true');
		} catch (error) {
			const errorMessage = `Failed to set user property with key: ${CUSTOM_DATES_CUSTOMIZE_SPOTLIGHT_DISMISSED}`;

			logSafeErrorWithoutCustomerDataWrapper(
				'polaris.custom-dates.customize.spotlight',
				errorMessage,
				error instanceof Error ? error : new UnknownError(errorMessage, error),
			);
		}
	};
